/**
 * Team tab (docs/plans/oncall-line-plan.md 2.3, 4 "Empty list guard", 5, 6.1,
 * 7.5, 7.7, 8; WP-11). Line admins only: OncallApp routes /oncall/team here
 * behind its admin guard, and every route it calls carries
 * requireOncallAdmin on the server.
 *
 * The people whose phones ring, in the order they ring, for the line of the
 * site in the header (switching site switches line):
 *
 *   - the list (GET /members): inactive members stay listed and are skipped;
 *     the server's warnings (fewer than three active members) show above it
 *   - add and edit (POST /members, PATCH /members/:id: name, US cell, the
 *     optional SiteOps user link)
 *   - reorder by drag or the Move buttons (PUT /members/order, every live id;
 *     409 ONCALL_ORDER_STALE reloads)
 *   - the active switch, and Remove (a soft delete: the row is kept, plan 5)
 *   - the last-member confirmation (plan 4): taking the last active member
 *     off the rotation asks first and sends confirm: true; a 409
 *     ONCALL_CONFIRM_REQUIRED from the server (the client's count was stale)
 *     asks the same question with the server's words
 *   - the PIN setter (PUT /members/:id/pin, plan 6.1)
 *   - Send test call (POST /members/:id/test-call) and its result, polled
 *     from GET /members until last_test_at changes (components/team/
 *     testCall.ts); disabled while ONCALL_ENABLED is off or
 *     ONCALL_PUBLIC_API_URL is unset (GET /health switches; plan 16 WP-08)
 *
 * A site without a line (404 ONCALL_NO_LINE) points to the Settings tab,
 * where the line is created. No SSE: the list is read on load, after every
 * write, on Refresh, and every TEST_POLL_MS while a test call is out.
 * The optional props exist for tests; OncallApp renders <TeamTab />.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Plus, Settings2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader, { RefreshButton } from '../../ui/PageHeader';
import EmptyState from '../../ui/EmptyState';
import ConfirmModal from '../../ui/ConfirmModal';
import { useToast } from '../../ui/Toast';
import { primaryBtnCls, secondaryBtnCls } from '../../ui/styles';
import {
  apiErrorCode,
  apiErrorMessage,
  getOncallHealth,
  listMembers,
  reorderMembers,
  sendTestCall,
  updateMember,
} from '../api/oncallApi';
import type { OncallHealth, OncallMember, OncallMemberList } from '../api/oncallApi';
import MemberList from '../components/team/MemberList';
import type { TeamMember } from '../components/team/MemberList';
import MemberFormModal from '../components/team/MemberFormModal';
import PinModal from '../components/team/PinModal';
import { TEST_POLL_LIMIT_MS, TEST_POLL_MS, testCallBlockReason, testOutcome } from '../components/team/testCall';
import type { PendingTest } from '../components/team/testCall';

/** Fewer active members than this and the page warns (plan 4, 7.7; members.js MIN_ACTIVE_MEMBERS). */
export const MIN_ACTIVE_MEMBERS = 3;

export interface TeamTabProps {
  /** How often a pending test call re-reads the list (default TEST_POLL_MS). */
  testPollMs?: number;
  /** How long a test call is waited for (default TEST_POLL_LIMIT_MS). */
  testPollLimitMs?: number;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'no-line' }
  | { kind: 'error'; message: string };

/** A deactivation or removal waiting for the admin's word. */
interface PendingChange {
  kind: 'deactivate' | 'delete';
  member: TeamMember;
  /** This member is the last active one: the confirm says so and confirm: true is sent. */
  lastMember: boolean;
  /** The server's explanation after a 409 ONCALL_CONFIRM_REQUIRED. */
  serverMessage?: string;
}

/** ids with `id` moved to `toIndex` (clamped); the same array when nothing moves. */
export function moveId(ids: string[], id: string, toIndex: number): string[] {
  const from = ids.indexOf(id);
  if (from < 0) return ids;
  const to = Math.max(0, Math.min(ids.length - 1, toIndex));
  if (to === from) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

function activeCountOf(members: TeamMember[]): number {
  return members.filter((m) => m.is_active).length;
}

export default function TeamTab({ testPollMs = TEST_POLL_MS, testPollLimitMs = TEST_POLL_LIMIT_MS }: TeamTabProps = {}) {
  const { currentSite, user } = useAuth();
  const { toast } = useToast();
  const siteId = currentSite?.id ?? null;
  const timeZone = currentSite?.timezone ?? null;

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [health, setHealth] = useState<OncallHealth | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [pinFor, setPinFor] = useState<TeamMember | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [pendingBusy, setPendingBusy] = useState(false);
  const [placing, setPlacing] = useState<Set<string>>(() => new Set());
  const [tests, setTests] = useState<PendingTest[]>([]);

  // Answers for a site that is no longer current are dropped.
  const siteRef = useRef(siteId);
  siteRef.current = siteId;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyList = useCallback((list: OncallMemberList) => {
    const items = (list.items ?? []) as TeamMember[];
    setMembers(items);
    setWarnings(Array.isArray(list.warnings) ? list.warnings : []);
  }, []);

  /** Re-read the list; `quiet` keeps the current screen on a failure (a toast says so). */
  const reload = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      const forSite = siteRef.current;
      if (!forSite) return;
      try {
        const list = await listMembers();
        if (!mounted.current || siteRef.current !== forSite) return;
        applyList(list);
        setLoad({ kind: 'ready' });
      } catch (err) {
        if (!mounted.current || siteRef.current !== forSite) return;
        if (apiErrorCode(err) === 'ONCALL_NO_LINE') {
          setMembers([]);
          setWarnings([]);
          setLoad({ kind: 'no-line' });
        } else if (quiet) {
          toast(apiErrorMessage(err, 'The member list could not be reloaded.'), { type: 'error' });
        } else {
          setLoad({ kind: 'error', message: apiErrorMessage(err, 'The member list could not be loaded.') });
        }
      }
    },
    [applyList, toast],
  );

  const loadHealth = useCallback(async () => {
    const forSite = siteRef.current;
    try {
      const h = await getOncallHealth();
      if (mounted.current && siteRef.current === forSite) setHealth(h);
    } catch {
      // Health only tunes the test-call button; the server still refuses with its own message.
      if (mounted.current && siteRef.current === forSite) setHealth(null);
    }
  }, []);

  // Load (and reload on a site switch: the line is the site's).
  useEffect(() => {
    if (!siteId) return;
    setLoad({ kind: 'loading' });
    setMembers([]);
    setWarnings([]);
    setHealth(null);
    setTests([]);
    setPlacing(new Set());
    reload();
    loadHealth();
  }, [siteId, reload, loadHealth]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([reload({ quiet: load.kind === 'ready' }), loadHealth()]);
    if (mounted.current) setRefreshing(false);
  };

  // -------------------------------------------------------------------------
  // Test calls: place, then poll for the result
  // -------------------------------------------------------------------------

  const testBlocked = testCallBlockReason(health);
  const testingIds = useMemo(() => new Set(tests.map((t) => t.memberId)), [tests]);

  const startTest = async (member: TeamMember) => {
    if (testBlocked || placing.has(member.id) || testingIds.has(member.id)) return;
    setPlacing((prev) => new Set(prev).add(member.id));
    try {
      await sendTestCall(member.id);
      if (!mounted.current) return;
      if (health?.switches?.dial_override_active) {
        toast(
          `Test call placed. It rang the test override number (ONCALL_DIAL_OVERRIDE_TO), not ${member.display_name}'s cell, so no result is recorded on the member.`,
          { type: 'info', duration: 8000 },
        );
      } else {
        toast(`Calling ${member.display_name}. They press 1 to pass the test.`, { type: 'info' });
        setTests((prev) => [
          ...prev.filter((t) => t.memberId !== member.id),
          { memberId: member.id, beforeAt: member.last_test_at ?? null, startedMs: Date.now() },
        ]);
      }
    } catch (err) {
      if (!mounted.current) return;
      const code = apiErrorCode(err);
      if (code === 'ONCALL_TEST_IN_PROGRESS') {
        toast(`A test call to ${member.display_name} is already ringing.`, { type: 'info' });
        setTests((prev) =>
          prev.some((t) => t.memberId === member.id)
            ? prev
            : [...prev, { memberId: member.id, beforeAt: member.last_test_at ?? null, startedMs: Date.now() }],
        );
      } else {
        toast(apiErrorMessage(err, 'The test call could not be placed.'), { type: 'error' });
      }
    } finally {
      if (mounted.current) {
        setPlacing((prev) => {
          const next = new Set(prev);
          next.delete(member.id);
          return next;
        });
      }
    }
  };

  // One poll while any test is out: read the list, settle the tests whose result arrived or timed out.
  const testsRef = useRef(tests);
  testsRef.current = tests;
  const hasTests = tests.length > 0;
  useEffect(() => {
    if (!hasTests) return undefined;
    let cancelled = false;
    const forSite = siteRef.current;
    const timer = window.setInterval(async () => {
      let list: OncallMemberList;
      try {
        list = await listMembers();
      } catch {
        return; // try again on the next tick; the limit still applies
      }
      if (cancelled || !mounted.current || siteRef.current !== forSite) return;
      const rows = (list.items ?? []) as TeamMember[];
      const byId = new Map(rows.map((m) => [m.id, m]));
      const now = Date.now();
      const settled: string[] = [];
      for (const t of testsRef.current) {
        const row = byId.get(t.memberId);
        const outcome = testOutcome(t, row);
        const name = row?.display_name ?? 'The member';
        if (outcome === 'passed') {
          toast(`${name} pressed 1: the test call passed.`, { type: 'success' });
          settled.push(t.memberId);
        } else if (outcome === 'failed') {
          toast(`${name}'s test call failed: nobody pressed 1.`, { type: 'error', duration: 8000 });
          settled.push(t.memberId);
        } else if (!row || now - t.startedMs >= testPollLimitMs) {
          if (row) toast(`No result yet from ${name}'s test call. It shows here when it arrives.`, { type: 'info' });
          settled.push(t.memberId);
        }
      }
      // Only the test fields change under the admin's feet; the rest waits for a reload.
      setMembers((prev) =>
        prev.map((m) => {
          const row = byId.get(m.id);
          return row ? { ...m, last_test_at: row.last_test_at, last_test_ok: row.last_test_ok } : m;
        }),
      );
      if (settled.length) setTests((prev) => prev.filter((t) => !settled.includes(t.memberId)));
    }, testPollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasTests, testPollMs, testPollLimitMs, toast]);

  // -------------------------------------------------------------------------
  // Reorder
  // -------------------------------------------------------------------------

  const move = async (memberId: string, toIndex: number) => {
    if (reordering) return;
    const before = members;
    const ids = moveId(before.map((m) => m.id), memberId, toIndex);
    if (ids.every((id, i) => id === before[i]?.id)) return;
    const byId = new Map(before.map((m) => [m.id, m]));
    setMembers(ids.map((id) => byId.get(id) as TeamMember));
    setReordering(true);
    try {
      const list = await reorderMembers(ids);
      if (!mounted.current) return;
      applyList(list);
    } catch (err) {
      if (!mounted.current) return;
      if (apiErrorCode(err) === 'ONCALL_ORDER_STALE') {
        toast('The list changed while you were moving it. It has been reloaded; try again.', { type: 'error' });
        await reload({ quiet: true });
      } else {
        setMembers(before);
        toast(apiErrorMessage(err, 'The new order could not be saved.'), { type: 'error' });
      }
    } finally {
      if (mounted.current) setReordering(false);
    }
  };

  // -------------------------------------------------------------------------
  // Active switch, Remove, and the last-member confirmation
  // -------------------------------------------------------------------------

  const isLastActive = (member: TeamMember) => member.is_active && activeCountOf(members) <= 1;

  /** Send the change; a 409 ONCALL_CONFIRM_REQUIRED turns into the confirm dialog. */
  const applyChange = async (change: PendingChange, confirm: boolean): Promise<boolean> => {
    const { member, kind } = change;
    const patch = kind === 'delete' ? { deleted: true } : { is_active: false };
    try {
      await updateMember(member.id, confirm ? { ...patch, confirm: true } : patch);
      if (!mounted.current) return true;
      toast(
        kind === 'delete'
          ? `${member.display_name} was removed from the list.`
          : `${member.display_name} is inactive and will not be rung.`,
        { type: 'success' },
      );
      await reload({ quiet: true });
      return true;
    } catch (err) {
      if (!mounted.current) return true;
      if (apiErrorCode(err) === 'ONCALL_CONFIRM_REQUIRED' && !confirm) {
        setPending({ ...change, lastMember: true, serverMessage: apiErrorMessage(err, '') || undefined });
        return false;
      }
      toast(apiErrorMessage(err, kind === 'delete' ? 'The member could not be removed.' : 'The member could not be deactivated.'), {
        type: 'error',
      });
      return true;
    }
  };

  const toggleActive = async (member: TeamMember, next: boolean) => {
    if (togglingId) return;
    if (!next) {
      if (isLastActive(member)) {
        setPending({ kind: 'deactivate', member, lastMember: true });
        return;
      }
      setTogglingId(member.id);
      await applyChange({ kind: 'deactivate', member, lastMember: false }, false);
      if (mounted.current) setTogglingId(null);
      return;
    }
    setTogglingId(member.id);
    try {
      await updateMember(member.id, { is_active: true });
      if (!mounted.current) return;
      toast(`${member.display_name} is active and rings in the hunt.`, { type: 'success' });
      await reload({ quiet: true });
    } catch (err) {
      if (mounted.current) toast(apiErrorMessage(err, 'The member could not be activated.'), { type: 'error' });
    } finally {
      if (mounted.current) setTogglingId(null);
    }
  };

  const askRemove = (member: TeamMember) => {
    setPending({ kind: 'delete', member, lastMember: isLastActive(member) });
  };

  const confirmPending = async () => {
    if (!pending || pendingBusy) return;
    const change = pending;
    setPendingBusy(true);
    const done = await applyChange(change, change.lastMember);
    if (!mounted.current) return;
    setPendingBusy(false);
    // A 409 swapped in the last-member question; otherwise the dialog is finished.
    if (done) setPending(null);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const activeCount = activeCountOf(members);
  const lineNumber = health?.line?.twilio_number_e164 ?? null;

  const header = (
    <PageHeader
      title="Team"
      icon={Users}
      count={load.kind === 'ready' ? members.length : null}
      subtitle="The people whose phones ring, in the order they ring. How many ring at once is set in Settings."
      actions={
        load.kind === 'ready' ? (
          <>
            <RefreshButton onClick={refresh} loading={refreshing} label="Reload the team" />
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              data-testid="oncall-member-add"
              className={primaryBtnCls}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add member
            </button>
          </>
        ) : null
      }
    />
  );

  let body: JSX.Element;
  if (load.kind === 'loading') {
    body = (
      <div className="flex justify-center py-12">
        <div role="status" aria-label="Loading" className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
      </div>
    );
  } else if (load.kind === 'no-line') {
    body = (
      <EmptyState
        icon={Users}
        variant="card"
        title="This site has no 24-Hour Line yet"
        hint="Create the line in Settings with the number bought for this site, then add the team here."
        action={
          <Link to="/oncall/settings" className={secondaryBtnCls} data-testid="oncall-team-go-settings">
            <Settings2 className="h-4 w-4" aria-hidden="true" />
            Go to Settings
          </Link>
        }
      />
    );
  } else if (load.kind === 'error') {
    body = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">{load.message}</span>
        <button type="button" onClick={() => { setLoad({ kind: 'loading' }); reload(); loadHealth(); }} className={secondaryBtnCls}>
          Retry
        </button>
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        {warnings.length > 0 && (
          <div role="alert" data-testid="oncall-team-warnings" className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            {warnings.map((w) => (
              <p key={w} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{w}</span>
              </p>
            ))}
          </div>
        )}
        {warnings.length === 0 && members.length > 0 && activeCount < MIN_ACTIVE_MEMBERS && (
          <p role="alert" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Keep at least {MIN_ACTIVE_MEMBERS} active members so a call always reaches someone.
          </p>
        )}
        {testBlocked && members.length > 0 && (
          <p data-testid="oncall-team-test-blocked" className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            {testBlocked}
          </p>
        )}
        {health?.switches?.dial_override_active && (
          <p data-testid="oncall-team-override" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Test mode: ONCALL_DIAL_OVERRIDE_TO is set, so every call the line places rings one test number instead of the
            member&apos;s cell.
          </p>
        )}
        {members.length === 0 ? (
          <EmptyState
            icon={Users}
            variant="card"
            title="Nobody is on the list yet"
            hint="Until someone is added, calls go to the fallback numbers, then voicemail. Add at least three people."
            action={
              <button type="button" onClick={() => { setEditing(null); setFormOpen(true); }} className={primaryBtnCls}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add member
              </button>
            }
          />
        ) : (
          <>
            <MemberList
              members={members}
              timeZone={timeZone}
              testing={testingIds}
              placing={placing}
              testBlocked={testBlocked}
              reorderDisabled={reordering}
              togglingId={togglingId}
              onMove={move}
              onToggleActive={toggleActive}
              onEdit={(m) => {
                setEditing(m);
                setFormOpen(true);
              }}
              onPin={(m) => setPinFor(m)}
              onTest={startTest}
              onRemove={askRemove}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Drag a row, or use the arrows, to change the order. Changes reach incoming calls within 30 seconds. Each
              member saves the line&apos;s number as a contact allowed through Do Not Disturb.
            </p>
          </>
        )}
      </div>
    );
  }

  const pendingTitle = pending
    ? pending.lastMember
      ? pending.kind === 'delete'
        ? `Remove ${pending.member.display_name}, the last active member?`
        : `Deactivate ${pending.member.display_name}, the last active member?`
      : `Remove ${pending.member.display_name}?`
    : '';
  const pendingMessage = pending ? (
    <div className="space-y-2">
      {pending.lastMember && (
        <p data-testid="oncall-last-member-warning">
          {pending.serverMessage ||
            'With nobody active, calls go to the fallback numbers, then voicemail. No one on this list will be rung.'}
        </p>
      )}
      {pending.kind === 'delete' && (
        <p>They leave the list and their phone stops ringing. The record of their calls is kept.</p>
      )}
    </div>
  ) : null;

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-4 px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5" data-testid="oncall-team-tab">
      {header}
      {body}

      <MemberFormModal
        isOpen={formOpen}
        member={editing}
        lineNumber={lineNumber}
        siteId={siteId ?? ''}
        currentUser={user ? { id: user.id, full_name: user.full_name, email: user.email } : null}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={(saved: OncallMember, mode) => {
          setFormOpen(false);
          setEditing(null);
          toast(mode === 'add' ? `${saved.display_name} was added to the end of the list.` : `${saved.display_name} was saved.`, {
            type: 'success',
          });
          reload({ quiet: true });
        }}
      />

      <PinModal
        isOpen={pinFor !== null}
        member={pinFor}
        timeZone={timeZone}
        onClose={() => setPinFor(null)}
        onSaved={(saved) => {
          setPinFor(null);
          toast(`A new PIN is set for ${saved.display_name}.`, { type: 'success' });
          reload({ quiet: true });
        }}
      />

      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => {
          if (!pendingBusy) setPending(null);
        }}
        onConfirm={confirmPending}
        title={pendingTitle}
        message={pendingMessage}
        confirmLabel={pending?.kind === 'delete' ? 'Remove' : 'Deactivate'}
        danger
        loading={pendingBusy}
      />
    </div>
  );
}
